/**
 * Live-trading one-click arm control (live-tennis V1b).
 *
 * The single visible truth for whether price clicks place orders. Disarmed
 * (always the state on load) the dashboard behaves exactly as before —
 * price clicks open the ticket panel. Armed, Kalshi price clicks submit
 * immediately at the displayed size, so the control:
 *
 *   • wears an unmissable armed treatment (filled red, pulsing dot);
 *   • keeps the size input beside the toggle — the packet's rule that the
 *     effective size is visible at the interaction surface;
 *   • doubles as the kill switch: one click disarms instantly, and the ack
 *     line (`OneClickStatus`, placed by each host — #1070) only ever reports
 *     what actually happened (pending → accepted / rejected / throttled),
 *     never a presumed success.
 *
 * Kalshi-only in V1 — Polymarket clicks keep the panel flow even when armed.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Zap } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import {
  useOneClickStore,
  oneClickCeiling,
  CHECK_OPEN_ORDERS,
  ackAfterDisarm,
  type OneClickAck,
  type OneClickLegAck,
} from '@/stores/oneClickStore';
import { useOneClickAccess } from '@/hooks/useOneClickAccess';
import { useGrantSlot } from '@/hooks/useGrantSlot';
import { useAuthStore } from '@/stores/authStore';
import { selectGrantsStatus } from '@/stores/grants';
import { StableWidthLabel } from '@/components/atoms/StableWidthLabel';
import { Tooltip } from '@/components/atoms/Tooltip';
import { LT2_READOUT_LINE_CLASS, LT2_READOUT_QUICK_REF, useReadoutBox } from '@/lib/trading/lt2Readout';
import { ArmedSizeField, ArmedSizeNotice, useArmedSize } from './ArmedSizeField';
import { useAttackAccessNotice } from './AttackArmControl';
import { ONE_CLICK_REVOKED_REASON } from './oneClickDisarmReasons';
import { useHotkeyLabel } from '@/hooks/useLiveHotkeys';
import { LEG_GLYPH, ackText, ackTone, legText } from './oneClickAckText';

/**
 * One line beneath the summary. A leg's mark and label never part at a line
 * end (taste D7); an inline pending leg's "…" is hidden by the slot, where
 * the "·" before it already marks it as part of the press. D13 — an earlier
 * press's late result is its own line, set off by a rule, with no mark: its
 * label already says "earlier".
 */
function AckLegLine({ leg }: { leg: OneClickLegAck }) {
  if (leg.earlier) {
    return (
      <span
        data-testid="one-click-ack-earlier"
        className={cn('border-border/60 block border-l-2 pl-1', ackTone(leg.status))}
      >
        {legText(leg)}
      </span>
    );
  }
  return (
    <span data-testid="one-click-ack-leg" className={cn('block', ackTone(leg.status))}>
      <span className="whitespace-nowrap">
        <span data-leg-glyph={leg.status}>{LEG_GLYPH[leg.status]} </span>
        {leg.label}
      </span>
      {leg.message ? ` — ${leg.message}` : ''}
    </span>
  );
}

/** D5 — the reason a confirmed profile that no longer grants one-click
 *  gives, on the status line. One sentence, shared with the hosts'
 *  revocation notice (useOneClickRevocation), which says it first. */
export const ACCESS_REVOKED_REASON = ONE_CLICK_REVOKED_REASON;
/** Access went from confirmed to unconfirmed (a new sign-in or role): the
 *  control holds its place disabled until the session confirms it again. */
export const ACCESS_UNCONFIRMED_REASON = 'Trading access is being confirmed for this sign-in';

/** D29 — ACCESS_UNCONFIRMED_REASON once the session has confirmed access:
 *  said in the past tense (it is no longer being confirmed), with how to
 *  come back, and no "— re-arm to trade" after it. */
const CONFIRMED_OFF_LINE = 'One-click off: turned off while your access was confirmed.';
const CONFIRMED_OFF_TITLE = 'Turned off while your access was confirmed. Click to re-arm.';

/** A sentence's closing stop, dropped so it can be followed by more text. */
function stripStop(text: string): string {
  return text.replace(/\.$/, '');
}

/** D5 — "One-click off: <reason>", unless the reason already names
 *  one-click ("One-click access was turned off…"), which would say it twice. */
function offLine(reason: string): string {
  return /^one-click\b/i.test(reason) ? reason : `One-click off: ${reason}`;
}

/** D5 — a reason the trader can act on by re-arming. One that says access
 *  was turned off for the account is not: re-arming cannot bring it back. */
function rearmable(reason: string, access: boolean): boolean {
  return access && !/for your account/i.test(reason);
}

export function OneClickArmControl() {
  const oneClickAccess = useOneClickAccess();
  // A restored grant keeps its place, disabled, until this session confirms it.
  const slot = useGrantSlot('oneClickAccess');
  // Per-trader server ceiling (traderRules.oneClickMaxContracts, D-020
  // LT2-010). Presentation clamp only — the relay enforces regardless.
  const serverCeiling = useAuthStore((s) => s.user?.oneClickMaxContracts ?? null);
  const effectiveMax = oneClickCeiling({ oneClickMaxContracts: serverCeiling });
  const armed = useOneClickStore((s) => s.armed);
  const sizeContracts = useOneClickStore((s) => s.sizeContracts);
  const arm = useOneClickStore((s) => s.arm);
  const disarm = useOneClickStore((s) => s.disarm);
  const disarmReason = useOneClickStore((s) => s.disarmReason);
  const setSizeContracts = useOneClickStore((s) => s.setSizeContracts);

  const grantsStatus = useAuthStore(selectGrantsStatus);
  const grantsReady = grantsStatus === 'ready';
  // Revocation hides the control and synchronously converges the local mode
  // back to safe on the next effect. The relay gate remains authoritative for
  // any request already in flight. D5 — losing access is an implicit change,
  // so it names its cause; D4 — it is a manual off (the trader can no longer
  // decide about an S hold's lost side here), so the hold clears with it. A
  // session that never had access this tab sets nothing.
  const hadAccess = useRef(oneClickAccess);
  useEffect(() => {
    const had = hadAccess.current;
    hadAccess.current = oneClickAccess;
    if (oneClickAccess) return;
    if (!had && !useOneClickStore.getState().armed) return;
    disarm(grantsReady ? ACCESS_REVOKED_REASON : ACCESS_UNCONFIRMED_REASON, { manual: true });
  }, [oneClickAccess, grantsReady, disarm]);

  // A persisted size may have been chosen by a trader with a larger explicit
  // ceiling. When the active identity changes, the hook converges the live
  // size to the new trader's authority, and says so above the controls,
  // instead of displaying an unusable value. Edits commit on Enter/blur only.
  const sizeBox = useArmedSize({
    size: sizeContracts,
    max: effectiveMax,
    setSize: setSizeContracts,
    limitName: 'one-click',
    // The box below renders only outside a hidden slot, enabled only with
    // confirmed access; a draft lives only while it can be typed in.
    active: slot !== 'hidden' && oneClickAccess,
  });

  if (slot === 'hidden') return null;

  return (
    // relative: the size notice's lane starts at this control's left edge.
    <div className="relative flex min-w-0 items-center gap-2">
      <ArmedSizeNotice box={sizeBox} />
      <button
        type="button"
        data-lt2-hotkey-control
        disabled={!oneClickAccess}
        onClick={() => {
          if (!oneClickAccess) return;
          // D4 — the trader's own off is the manual one: it also resets an S
          // hold. Every board-driven disarm keeps the hold.
          if (armed) disarm(undefined, { manual: true }); else arm();
        }}
        title={
          !oneClickAccess
            ? grantsStatus === 'error'
              ? `${ACCESS_HELD_TITLE} Retry checks again now.`
              : ACCESS_HELD_TITLE
            : armed
            ? 'One-click trading ARMED — Kalshi price clicks place orders immediately. Click to disarm.'
            : disarmReason === ACCESS_UNCONFIRMED_REASON
              ? CONFIRMED_OFF_TITLE
              : disarmReason
              ? `Turned off: ${stripStop(disarmReason)}. Click to re-arm.`
              : 'Arm one-click trading: Kalshi price clicks place orders immediately at the size shown (session-only; disarms on refresh).'
        }
        className={cn(
          'flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors',
          armed
            ? 'border-negative bg-negative/90 text-white'
            : oneClickAccess
              ? 'border-border/50 bg-muted/50 text-muted-foreground hover:text-foreground'
              : 'border-border/40 bg-muted/30 text-muted-foreground/50',
        )}
      >
        {/* #1070: one width for both faces, so arming never slides the
            button out from under the pointer or pushes its neighbours. */}
        <StableWidthLabel
          active={armed ? 1 : 0}
          faces={[
            <><Zap size={13} aria-hidden />1-Click</>,
            <><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" aria-hidden /><Zap size={13} aria-hidden />1-CLICK ARMED</>,
          ]}
        />
      </button>
      <ArmedSizeField
        box={sizeBox}
        label="One-click order size"
        quickRef={`One-click order size (contracts, max ${effectiveMax}). Enter or click away to apply. Esc reverts.`}
        disabled={!oneClickAccess}
        emphasized={armed}
        widthPad="2.25rem"
        noticeOnBox={false}
      />
    </div>
  );
}

/** D29 — the 1-Click title while a restored grant is not yet confirmed. */
const ACCESS_HELD_TITLE = 'One-click stays off until G1 confirms your access for this session.';

/**
 * D29 — a restored one-click grant this session has not confirmed yet: the
 * controls sit disabled, and this line says why in visible text (it used to
 * be only a hover title). A failed read offers Retry, which reads the
 * profile again now. No tone.
 */
/** The access line is showing: a restored one-click grant not confirmed yet. */
function useAccessConfirming(): boolean {
  return useAuthStore((s) => s.user?.oneClickAccess === true && selectGrantsStatus(s) !== 'ready');
}

function AccessConfirmLine() {
  const confirming = useAccessConfirming();
  const status = useAuthStore(selectGrantsStatus);
  const refreshMe = useAuthStore((s) => s.refreshMe);
  if (!confirming) return null;
  if (status === 'unknown') {
    return (
      <span role="status" className="text-muted-foreground min-w-0 max-w-[300px] whitespace-normal text-[11px] leading-tight">
        Confirming trading access…
      </span>
    );
  }
  return (
    <span role="status" className="min-w-0 max-w-[300px] whitespace-normal text-[11px] leading-tight text-amber-400">
      Couldn&apos;t confirm trading access ·{' '}
      <button
        type="button"
        onClick={(e) => {
          e.currentTarget.blur();
          refreshMe().catch(() => {});
        }}
        title="Reads your trading access again now."
        className="hover:text-foreground font-semibold underline"
      >
        Retry
      </button>
    </span>
  );
}

/**
 * D4 / B9 — a held S, on the status line whether one-click is armed or not.
 * The hold refuses every both-sides key until the lost side is confirmed (a
 * known receipt, or seen in open orders), the trader turns 1-Click off, or
 * they release it here after checking. Releasing never disarms.
 */
function TwoSidedHoldLine() {
  const hold = useOneClickStore((s) => s.twoSidedHold);
  const release = useOneClickStore((s) => s.releaseTwoSidedHold);
  const bound = useHotkeyLabel('two-sided');
  if (hold == null) return null;
  const key = bound === '—' ? 'S' : bound;
  return (
    <span
      role="status"
      data-testid="two-sided-hold-line"
      className="min-w-0 max-w-[300px] whitespace-normal break-words text-[11px] leading-tight text-amber-400"
    >
      {key} held — earlier {hold.label} side unconfirmed; {CHECK_OPEN_ORDERS}{' '}
      <button
        type="button"
        // An LT2 control: after the click the keyboard goes straight back to
        // the aimed card (the line usually goes with the hold).
        data-lt2-hotkey-control=""
        onClick={(e) => {
          e.currentTarget.blur();
          release();
        }}
        title={`Releases the ${key} hold after you have checked open orders. One-click and Attack stay armed.`}
        className="hover:text-foreground whitespace-nowrap rounded border border-amber-400/60 px-1 font-sans font-semibold leading-none"
      >
        I checked, release {key}
      </button>
    </span>
  );
}

/**
 * The one-click status line: why one-click turned itself off, or what the
 * last armed press did. Renders nothing when there is nothing to report.
 * Hosts place it; a host that must not reflow wraps it in
 * {@link OneClickStatusSlot} instead.
 */
export function OneClickStatus() {
  const armed = useOneClickStore((s) => s.armed);
  const disarmReason = useOneClickStore((s) => s.disarmReason);
  const lastAck = useOneClickStore((s) => s.lastAck);
  const disarmSeq = useOneClickStore((s) => s.disarmSeq);
  const oneClickAccess = useOneClickAccess();
  // D5 — a line written after the last disarm: a pull or an order whose
  // result came back once one-click was already off.
  const afterOff = ackAfterDisarm({ armed, lastAck, disarmSeq });
  // D5 — Attack turned off for the account, until the next press's line.
  const attackNotice = useAttackAccessNotice((s) => s.notice);
  const showAttackNotice = attackNotice != null && (lastAck?.seq ?? null) === attackNotice.afterSeq;
  // D29 — while the access line says "Confirming trading access…", the off
  // reason that says the same thing is not repeated under it.
  const confirming = useAccessConfirming();
  const unconfirmedOff = disarmReason === ACCESS_UNCONFIRMED_REASON;
  return (
    <>
      {/* First: a held S refuses a key until the trader acts, so its line
          and its release are never below the fixed box's fold behind the
          press that caused it. */}
      <TwoSidedHoldLine />
      <AccessConfirmLine />
      {showAttackNotice && (
        <span role="status" className="min-w-0 max-w-[300px] whitespace-normal text-xs text-amber-400">
          {attackNotice.text}
        </span>
      )}
      {!armed && disarmReason && !(unconfirmedOff && confirming) && (
        <span role="status" className="min-w-0 max-w-[300px] whitespace-normal text-xs text-amber-400">
          {unconfirmedOff ? (
            <>
              {CONFIRMED_OFF_LINE}
              {oneClickAccess ? ' Click 1-Click to re-arm.' : ''}
            </>
          ) : (
            <>
              {offLine(disarmReason)}
              {rearmable(disarmReason, oneClickAccess) ? ' — re-arm to trade' : ''}
            </>
          )}
        </span>
      )}
      {afterOff && (
        <span
          role="status"
          className={cn(
            'min-w-0 max-w-[300px] whitespace-normal break-words font-mono text-[11px] leading-tight',
            ackTone(afterOff.status),
          )}
          title={`After 1-Click off: ${ackText(afterOff)}`}
        >
          After 1-Click off: {ackText(afterOff)}
          {afterOff.legs?.map((leg, i) => <AckLegLine key={i} leg={leg} />)}
        </span>
      )}
      {armed && lastAck && (
        <span
          role="status"
          // Bounded status region (Phase 3 Loom hard failure): horizontal
          // growth is CAPPED and the full reason WRAPS inside it — a long
          // rejection must never stretch the toolbar, compress the sibling
          // controls, or hide its tail behind a hover. The title merely
          // duplicates the visible text for copy/hover convenience.
          className={cn(
            'min-w-0 max-w-[300px] whitespace-normal break-words font-mono text-[11px] leading-tight',
            ackTone(lastAck.status),
          )}
          title={ackText(lastAck)}
        >
          {ackText(lastAck)}
          {/* #724 — a two-sided press lists its legs beneath the summary, in
              the same bounded region, so a refused side is always on screen
              and never behind a hover. Each leg carries its own tone: the
              summary says what happened to the PRESS, a leg says what
              happened to that ORDER. */}
          {lastAck.legs?.map((leg, i) => <AckLegLine key={i} leg={leg} />)}
        </span>
      )}
    </>
  );
}

/**
 * What the status box shows, as one string. A line the hotkey listener lays
 * over the box belongs to exactly this (#1080), so any new or updated receipt
 * (an ack keeps its seq from pending to accepted) puts the line away.
 */
function statusContentKey(armed: boolean, disarmReason: string | null, lastAck: OneClickAck | null, holdLabel: string | null): string {
  return `${holdLabel ?? ''}#${statusAckKey(armed, disarmReason, lastAck)}`;
}

function statusAckKey(armed: boolean, disarmReason: string | null, lastAck: OneClickAck | null): string {
  // D5 — while off, a line written after the disarm is shown too.
  if (!armed) return `off|${disarmReason ?? ''}|${lastAck?.seq ?? ''}|${lastAck ? ackText(lastAck) : ''}`;
  if (!lastAck) return 'armed';
  return [lastAck.seq, ackText(lastAck), ...(lastAck.legs ?? []).map(legText)].join('|');
}

/** What {@link OneClickStatus} shows beyond the strip's own fields, as one
 *  string: the access line's state, a shown Attack notice, the re-arm hint. */
function useStatusExtras(lastAck: OneClickAck | null): string {
  const accessLine = useAuthStore((s) =>
    s.user?.oneClickAccess === true && selectGrantsStatus(s) !== 'ready' ? selectGrantsStatus(s) : '');
  const notice = useAttackAccessNotice((s) => s.notice);
  const oneClickAccess = useOneClickAccess();
  const shownNotice = notice != null && (lastAck?.seq ?? null) === notice.afterSeq ? notice.text : '';
  return `${accessLine}|${shownNotice}|${oneClickAccess ? 1 : 0}`;
}

// Whole literal classes per line count (never interpolated), so Tailwind's
// scan sees them.
const STATUS_BOX_HEIGHT = { 2: 'h-[calc(2*1.25*11px)]', 3: 'h-[calc(3*1.25*11px)]', 6: 'h-[calc(6*1.25*11px)]' } as const;

/**
 * {@link OneClickStatus} in a fixed box (#1070): `lines` ack lines (two by
 * default), as wide as whatever its host gives it (it never sizes the host).
 * A narrower host asks for more lines so the same receipt still fits (#1125).
 * It is always held while the host shows it, armed or not, so arming and
 * every ack, leg line or disarm reason change text inside a box that never
 * resizes — nothing around it moves. A two-sided press's legs run inline
 * after the summary here, so the common two-leg ack fits; anything longer
 * scrolls inside the box, and a visible "more" cue says so — a platform
 * scrollbar may not be drawn until the trader already knows to scroll, so it
 * is not the affordance.
 */
export function OneClickStatusSlot({ lines = 2 }: { lines?: 2 | 3 | 6 } = {}) {
  const armed = useOneClickStore((s) => s.armed);
  const lastAck = useOneClickStore((s) => s.lastAck);
  const disarmReason = useOneClickStore((s) => s.disarmReason);
  const holdLabel = useOneClickStore((s) => s.twoSidedHold?.label ?? null);
  // The status lines that do not come from the strip's own fields (the
  // access line, the Attack notice, the re-arm hint) change the box's text
  // too: they re-measure it and own any line laid over it, as an ack does.
  const extras = useStatusExtras(lastAck);
  const boxRef = useRef<HTMLDivElement>(null);
  // `overflowing`: the text is taller than the box (it stays keyboard-
  // reachable for as long as that holds); `hiddenBelow`: some of it is
  // still below the fold right now (the visible cue).
  const [overflowing, setOverflowing] = useState(false);
  const [hiddenBelow, setHiddenBelow] = useState(false);
  // Focusable while it overflows or while it holds focus (#1080). A line the
  // hotkey listener shows here belongs to the text it was shown over.
  const readout = useReadoutBox(boxRef, `${extras}#${statusContentKey(armed, disarmReason, lastAck, holdLabel)}`);
  const focusable = overflowing || readout.holdsFocus;
  const measure = useCallback(() => {
    const box = boxRef.current;
    if (!box) return;
    // 1px slack: fractional line boxes round differently per platform.
    setOverflowing(box.clientHeight < box.scrollHeight - 1);
    setHiddenBelow(box.scrollTop + box.clientHeight < box.scrollHeight - 1);
  }, []);
  // Re-measure whenever the text can change; a new ack starts at its top.
  useLayoutEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = 0;
    measure();
  }, [lastAck, disarmReason, armed, holdLabel, extras, measure]);
  return (
    // w-0 + min-w-full: as wide as the host's other content, never wider.
    // The quick reference appears only while the box is focusable, and on
    // hover only: keys pressed in the box act on the aimed card, so a trader
    // may stay in it, and a focus tooltip would sit over the board below.
    <Tooltip
      content={focusable ? LT2_READOUT_QUICK_REF : null}
      openOnFocus={false}
      position="bottom"
      wrapperClassName="block w-0 min-w-full"
    >
      <div
        ref={boxRef}
        data-testid="one-click-status-slot"
        // #1080: keys pressed in here act on the aimed card; the value names
        // this box in anything the hotkey listener says about it.
        data-lt2-readout="status"
        tabIndex={focusable ? 0 : undefined}
        role={focusable ? 'region' : undefined}
        aria-label={focusable ? 'One-click status (scrollable)' : undefined}
        onFocus={readout.onFocus}
        onBlur={readout.onBlur}
        onScroll={measure}
        onWheel={readout.onWheel}
        // Scroll keys stay here and Escape leaves the box with the aim kept;
        // every other key reaches the hotkey listener (#1080).
        onKeyDown={readout.onKeyDown}
        // `lines` × 11px × leading-tight (1.25). The box sets the type, and
        // its children drop their own size/cap, so every line box matches
        // (an inherited 1.5 line-height or the disarm reason's 12px would
        // clip the last line). Legs go inline, "·"-led, same text and tone.
        // pr-12 keeps text clear of the cue.
        className={cn(STATUS_BOX_HEIGHT[lines], "overflow-y-auto pr-12 text-[11px] leading-tight [&>[role=status]]:block [&>[role=status]]:max-w-none [&>[role=status]]:text-[11px] [&>[role=status]]:leading-tight [&_[data-testid=one-click-ack-leg]]:inline [&_[data-testid=one-click-ack-leg]]:before:content-['_·_'] [&_[data-leg-glyph=pending]]:hidden")}
      >
        <OneClickStatus />
      </div>
      {hiddenBelow && (
        <span
          aria-hidden
          data-testid="one-click-status-more"
          className="text-muted-foreground pointer-events-none absolute bottom-0 right-1 font-mono text-[10px] leading-tight"
        >
          more ↓
        </span>
      )}
      {/* #1080: over the box, out of flow, two lines at most; the receipt
          underneath is untouched and returns when the line goes. */}
      {readout.line && (
        <span role="status" data-testid="lt2-readout-line" className={LT2_READOUT_LINE_CLASS}>
          {readout.line}
        </span>
      )}
    </Tooltip>
  );
}
